import axios from "axios";

export enum ConditionType {
  DELIVERY = "DELIVERY",
  PAYMENT = "PAYMENT",
}
export interface Condition {
  id: string | null;
  title: string;
  type: ConditionType;
}

export interface Sector {
  id: string | null;
  title: string;
}

export interface Warehouse {
  id: string | null;
  title: string;
}

export interface ForwardingAgency {
  id: string | null;
  title: string;
}

export interface LockedPickupDate {
  id: string | null;
  lockedAt: Date | undefined;
}


interface LockedPickupDateApi {
  list(): Promise<LockedPickupDate[]>;
  add(lockedPickupDate: LockedPickupDate): Promise<LockedPickupDate>;
  delete(lockedPickupDate: LockedPickupDate): Promise<void>;
}


interface ConditionApi {
  listDeliveryCondition(): Promise<Condition[]>;
  listPaymentCondition(): Promise<Condition[]>;
  addCondition(condition: Condition): Promise<string>;
  editCondition(condition: Condition): Promise<void>;
  deleteCondition(condition: Condition): Promise<void>;
}

interface SectorApi {
  listSectors(): Promise<Sector[]>;
  addSector(sector: Sector): Promise<string>;
  editSector(sector: Sector): Promise<void>;
  deleteSector(sector: Sector): Promise<void>;
}

interface WarehouseApi {
  listWarehouses(): Promise<Warehouse[]>;
  addWarehouse(warehouse: Warehouse): Promise<string>;
  editWarehouse(warehouse: Warehouse): Promise<void>;
  deleteWarehouse(warehouse: Warehouse): Promise<void>;
}

interface ForwardingAgencyApi {
  listForwardingAgency(): Promise<ForwardingAgency[]>;
  addForwardingAgency(forwardingAgency: ForwardingAgency): Promise<string>;
  editForwardingAgency(forwardingAgency: ForwardingAgency): Promise<void>;
  deleteForwardingAgency(forwardingAgency: ForwardingAgency): Promise<void>;
}


export const lockedPickupDateApi: LockedPickupDateApi = {
  async list() {
    return (await axios.get("/api/misc/locked_pickup_dates")).data.map((raw:LockedPickupDate) => {
      return {...raw,
        lockedAt: raw.lockedAt ? raw.lockedAt = new Date(raw.lockedAt) : raw.lockedAt = undefined
      } as LockedPickupDate});
  },
  async add(lockedPickupDate) {
    return (await axios.post("/api/misc/locked_pickup_dates", lockedPickupDate)).data
  },
  async delete(lockedPickupDate) {
    return await axios.delete(`/api/misc/locked_pickup_dates/${lockedPickupDate.id}`);
  },
};

export const sectorApi: SectorApi = {
  async listSectors() {
    return (await axios.get("/api/misc/sectors")).data;
  },
  async editSector(sector) {
    return await axios.put(`/api/misc/sectors/${sector.id}`, sector);
  },
  async addSector(sector) {
    return (await axios.post("/api/misc/sectors", sector)).data;
  },
  async deleteSector(sector) {
    return await axios.delete(`/api/misc/sectors/${sector.id}`);
  },
};

export const warehouseApi: WarehouseApi = {
  async listWarehouses() {
    return (await axios.get("/api/misc/warehouses")).data;
  },
  async editWarehouse(warehouse) {
    return await axios.put(`/api/misc/warehouses/${warehouse.id}`, warehouse);
  },
  async addWarehouse(warehouse) {
    return (await axios.post("/api/misc/warehouses", warehouse)).data;
  },
  async deleteWarehouse(warehouse) {
    return await axios.delete(`/api/misc/warehouses/${warehouse.id}`);
  },
};

export const forwardingAgencyApi: ForwardingAgencyApi = {
  async listForwardingAgency() {
    return (await axios.get("/api/misc/forwarding-agency")).data;
  },
  async editForwardingAgency(forwardingAgency) {
    return await axios.put(`/api/misc/forwarding-agency/${forwardingAgency.id}`, forwardingAgency);
  },
  async addForwardingAgency(forwardingAgency) {
    return (await axios.post("/api/misc/forwarding-agency", forwardingAgency)).data;
  },
  async deleteForwardingAgency(forwardingAgency) {
    return await axios.delete(`/api/misc/forwarding-agency/${forwardingAgency.id}`);
  },
};

export const conditionApi: ConditionApi = {
  async listDeliveryCondition() {
    const result = (await axios.get("/api/misc/conditions/delivery")).data;
    if (result) {
      return result.map(
        (v: Condition) =>
          ({
            ...v,
            type: ConditionType.DELIVERY,
          } as Condition)
      );
    }
    return [];
  },

  async listPaymentCondition() {
    const result = (await axios.get("/api/misc/conditions/payment")).data;
    if (result) {
      return result.map(
        (v: Condition) =>
          ({
            ...v,
            type: ConditionType.PAYMENT,
          } as Condition)
      );
    }
    return [];
  },

  async editCondition(condition) {
    if (condition.type === ConditionType.PAYMENT) {
      return await axios.put(
        `/api/misc/conditions/payment/${condition.id}`,
        condition
      );
    }
    if (condition.type === ConditionType.DELIVERY) {
      return await axios.put(
        `/api/misc/conditions/delivery/${condition.id}`,
        condition
      );
    }
  },

  async addCondition(condition) {
    if (condition.type === ConditionType.PAYMENT) {
      return (await axios.post("/api/misc/conditions/payment", condition)).data;
    }
    if (condition.type === ConditionType.DELIVERY) {
      return (await axios.post("/api/misc/conditions/delivery", condition))
        .data;
    }
  },

  async deleteCondition(condition) {
    if (condition.type === ConditionType.PAYMENT) {
      return await axios.delete(`/api/misc/conditions/payment/${condition.id}`);
    }
    if (condition.type === ConditionType.DELIVERY) {
      return await axios.delete(
        `/api/misc/conditions/delivery/${condition.id}`
      );
    }
  },
};
